import { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Plus, Utensils, Flame, Beef, Wheat, Droplet, Search, ChevronRight, Edit3, Apple, X, Trash2, Check, Image, Save, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import { mealAPI, mealPlanAPI, foodsAPI } from '../utils/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { getTodayString } from '../utils/dateUtils';

function AddCustomMeal() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get('date') || getTodayString();
  const typeParam = searchParams.get('type') || 'Lunch';
  const returnPath = searchParams.get('return') || null;
  const initialTab = searchParams.get('tab') === 'meals' ? 'meals' : 'foods';

  const [mode, setMode] = useState('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(initialTab); // 'foods' or 'meals'
  const [allMeals, setAllMeals] = useState([]);
  const [allFoods, setAllFoods] = useState([]);
  const [searchLoading, setSearchLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Single food quantity (Foods tab)
  const [foodQuantity, setFoodQuantity] = useState('1');
  const [foodUnit, setFoodUnit] = useState('');
  const [calculatedMacros, setCalculatedMacros] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Multi-ingredient meal builder (Meals tab)
  const [mealIngredients, setMealIngredients] = useState([]);
  const [addingIngredient, setAddingIngredient] = useState(null); // food being added with quantity
  const [ingredientQuantity, setIngredientQuantity] = useState('1');
  const [ingredientUnit, setIngredientUnit] = useState('');
  const [isEditingMacros, setIsEditingMacros] = useState(false);
  
  // Image upload for custom meals
  const [mealImageUrl, setMealImageUrl] = useState('');
  // Save to My Meals toggle
  const [saveToMyMeals, setSaveToMyMeals] = useState(true);

  const [name, setName] = useState('');
  const [calories, setCalories] = useState('');
  const [protein, setProtein] = useState('');
  const [carbs, setCarbs] = useState('');
  const [fat, setFat] = useState('');
  const [mealType, setMealType] = useState(typeParam);
  const [date, setDate] = useState(dateParam);
  const [saveToLibrary, setSaveToLibrary] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Search state for real-time USDA search
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Recently logged foods for quick access
  const [recentFoods, setRecentFoods] = useState([]);
  
  // My Meals (user's saved custom meals)
  const [myMeals, setMyMeals] = useState([]);
  const [myMealsLoading, setMyMealsLoading] = useState(false);
  
  // Search history for autocomplete
  const [searchHistory, setSearchHistory] = useState([]);
  const [showSearchHistory, setShowSearchHistory] = useState(false);

  const mealTypes = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];

  const fetchData = useCallback(() => {
    setSearchLoading(true);
    Promise.all([
      mealAPI.getMeals(), 
      foodsAPI.getFoods(),
      foodsAPI.getRecentFoods(8),
      foodsAPI.getSearchHistory(8),
      mealAPI.getMyMeals()
    ])
      .then(([mealsRes, foodsRes, recentRes, historyRes, myMealsRes]) => {
        const meals = mealsRes.data || [];
        const foods = foodsRes.data.foods || [];
        const recent = recentRes.data.results || [];
        const history = historyRes.data.history || [];
        const myMealsList = myMealsRes.data.meals || [];
        setAllMeals(meals);
        setAllFoods(foods);
        setRecentFoods(recent);
        setSearchHistory(history);
        setMyMeals(myMealsList);
      })
      .catch(err => console.error('Failed to fetch:', err))
      .finally(() => setSearchLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Real-time search with USDA fallback
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    
    const timeoutId = setTimeout(() => {
      setIsSearching(true);
      foodsAPI.searchFoods(searchQuery.trim(), 25)
        .then(res => {
          setSearchResults(res.data.results || []);
        })
        .catch(err => {
          console.error('Search failed:', err);
          setSearchResults([]);
        })
        .finally(() => setIsSearching(false));
    }, 300); // Debounce 300ms
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Calculate total macros from ingredients
  const calculateTotalMacros = useCallback(() => {
    if (mealIngredients.length === 0) {
      return { calories: 0, protein: 0, carbs: 0, fat: 0 };
    }
    return mealIngredients.reduce((acc, ing) => ({
      calories: acc.calories + (ing.macros?.calories || 0),
      protein: acc.protein + (ing.macros?.protein || 0),
      carbs: acc.carbs + (ing.macros?.carbs || 0),
      fat: acc.fat + (ing.macros?.fat || 0)
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
  }, [mealIngredients]);

  // Update macro fields when ingredients change (only if not manually editing)
  useEffect(() => {
    if (!isEditingMacros && mealIngredients.length > 0) {
      const totals = calculateTotalMacros();
      setCalories(String(Math.round(totals.calories)));
      setProtein(String(Math.round(totals.protein)));
      setCarbs(String(Math.round(totals.carbs)));
      setFat(String(Math.round(totals.fat)));
    }
  }, [mealIngredients, isEditingMacros, calculateTotalMacros]);

  // Use search results when searching, otherwise show all foods
  const filteredFoods = searchQuery.trim()
    ? searchResults.filter(f => f.source !== 'user') // Foods from search API (exclude user meals shown in meals tab)
    : allFoods;

  // Filter meals based on search query (local filtering for meals)
  const filteredMeals = searchQuery.trim()
    ? allMeals.filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : allMeals;

  // Select existing meal from library (Meals tab - quick log)
  const selectExistingMeal = (meal) => {
    setSelectedItem({ ...meal, itemType: 'meal' });
    setName(meal.name);
    setCalories(String(meal.calories));
    setProtein(String(meal.protein));
    setCarbs(String(meal.carbs));
    setFat(String(meal.fat));
    setMode('confirm');
  };

  // Select food for single logging (Foods tab) - handles both API formats
  const selectFood = async (food) => {
    const foodName = food.display_name || food.food_name || food.name;
    const foodId = food.food_id || food.fdc_id;
    
    // Record search history if there was a search query
    if (searchQuery.trim()) {
      foodsAPI.recordSearch(searchQuery.trim(), foodId, foodName).catch(() => {});
      setShowSearchHistory(false);
    }
    
    // Cache USDA food if it's a live result (not yet in our database)
    if (food.source === 'usda_live' && food.fdc_id) {
      try {
        // Cache in background - don't block UI
        foodsAPI.cacheFromUSDA(food.fdc_id).catch(err => {
          console.log('Background cache failed (may already be cached):', err);
        });
      } catch (err) {
        console.log('Cache attempt failed:', err);
      }
    }
    
    // Normalize units - handle both formats:
    // - allFoods API: { unit: "g", grams_per_unit: 1, display_name: "g" }
    // - search API: { unit_name: "g", grams_per_unit: 1, is_estimated: false }
    const rawUnits = food.units || [];
    const normalizedUnits = rawUnits.map(u => ({
      unit: u.unit || u.unit_name || 'g',
      grams_per_unit: u.grams_per_unit || 1,
      display_name: u.display_name || (u.is_estimated ? `${u.unit_name} (est.)` : (u.unit_name || u.unit || 'g'))
    }));
    
    // Find default unit
    const defaultUnitName = food.default_unit || 
      (rawUnits.find(u => u.is_default)?.unit_name) || 
      (rawUnits.find(u => u.is_default)?.unit) ||
      normalizedUnits[0]?.unit || 'g';
    const defaultUnitInfo = normalizedUnits.find(u => u.unit === defaultUnitName) || normalizedUnits[0] || { unit: 'g', grams_per_unit: 1, display_name: 'g' };
    
    // Calculate per-100g values (some foods have both formats, prefer per_100g if available)
    const calories_per_100g = food.calories_per_100g ?? Math.round((food.cal_per_g || 0) * 100);
    const protein_per_100g = food.protein_per_100g ?? Math.round((food.protein_per_g || 0) * 100 * 10) / 10;
    const carbs_per_100g = food.carbs_per_100g ?? Math.round((food.carbs_per_g || 0) * 100 * 10) / 10;
    const fat_per_100g = food.fat_per_100g ?? Math.round((food.fat_per_g || 0) * 100 * 10) / 10;
    
    setSelectedItem({ 
      ...food, 
      itemType: 'food',
      name: food.display_name || food.food_name || food.name,
      calories_per_100g,
      protein_per_100g,
      carbs_per_100g,
      fat_per_100g,
      units: normalizedUnits
    });
    setFoodUnit(defaultUnitInfo.unit);
    
    // Set default quantity based on unit type
    // For grams, default to 100g to match the preview display (per 100g)
    // For other units (oz, serving, etc.), default to 1
    const isGramUnit = defaultUnitInfo.unit === 'g' || defaultUnitInfo.unit === 'gram' || defaultUnitInfo.unit === 'grams';
    const defaultQuantity = isGramUnit ? '100' : '1';
    setFoodQuantity(defaultQuantity);
    
    // Calculate macros for default serving
    const qtyNum = parseFloat(defaultQuantity);
    const grams = (defaultUnitInfo.grams_per_unit || 1) * qtyNum;
    const factor = grams / 100;
    setCalculatedMacros({
      calories: Math.round(calories_per_100g * factor),
      protein: Math.round(protein_per_100g * factor * 10) / 10,
      carbs: Math.round(carbs_per_100g * factor * 10) / 10,
      fat: Math.round(fat_per_100g * factor * 10) / 10
    });
    setMode('food-detail');
  };

  // Start adding ingredient to meal builder (Meals tab) - handles both formats
  const startAddIngredient = (food) => {
    // Cache USDA food if it's a live result (not yet in our database)
    if (food.source === 'usda_live' && food.fdc_id) {
      foodsAPI.cacheFromUSDA(food.fdc_id).catch(err => {
        console.log('Background cache failed (may already be cached):', err);
      });
    }
    
    // Normalize units - handle both formats
    const rawUnits = food.units || [];
    const normalizedUnits = rawUnits.map(u => ({
      unit: u.unit || u.unit_name || 'g',
      grams_per_unit: u.grams_per_unit || 1,
      display_name: u.display_name || (u.is_estimated ? `${u.unit_name} (est.)` : (u.unit_name || u.unit || 'g'))
    }));
    
    // Find default unit
    const defaultUnitName = food.default_unit || 
      (rawUnits.find(u => u.is_default)?.unit_name) || 
      (rawUnits.find(u => u.is_default)?.unit) ||
      normalizedUnits[0]?.unit || 'g';
    
    // Calculate per-100g values
    const calories_per_100g = food.calories_per_100g ?? Math.round((food.cal_per_g || 0) * 100);
    const protein_per_100g = food.protein_per_100g ?? Math.round((food.protein_per_g || 0) * 100 * 10) / 10;
    const carbs_per_100g = food.carbs_per_100g ?? Math.round((food.carbs_per_g || 0) * 100 * 10) / 10;
    const fat_per_100g = food.fat_per_100g ?? Math.round((food.fat_per_g || 0) * 100 * 10) / 10;
    
    const convertedFood = {
      ...food,
      food_id: food.food_id || food.fdc_id,
      name: food.display_name || food.food_name || food.name,
      calories_per_100g,
      protein_per_100g,
      carbs_per_100g,
      fat_per_100g,
      default_unit: defaultUnitName,
      units: normalizedUnits
    };
    
    setAddingIngredient(convertedFood);
    setIngredientUnit(defaultUnitName);
    setIngredientQuantity('1');
  };

  // Calculate macros for ingredient being added
  const getIngredientMacros = (food, qty, unit) => {
    const qtyNum = parseFloat(qty) || 0;
    if (qtyNum <= 0) return null;
    const unitInfo = food.units.find(u => u.unit === unit);
    if (!unitInfo) return null;
    const grams = qtyNum * unitInfo.grams_per_unit;
    const factor = grams / 100;
    return {
      calories: Math.round(food.calories_per_100g * factor),
      protein: Math.round(food.protein_per_100g * factor * 10) / 10,
      carbs: Math.round(food.carbs_per_100g * factor * 10) / 10,
      fat: Math.round(food.fat_per_100g * factor * 10) / 10
    };
  };

  // Confirm adding ingredient to meal
  const confirmAddIngredient = () => {
    if (!addingIngredient) return;
    const macros = getIngredientMacros(addingIngredient, ingredientQuantity, ingredientUnit);
    if (!macros) {
      toast.error('Invalid quantity');
      return;
    }
    const unitInfo = addingIngredient.units.find(u => u.unit === ingredientUnit);
    const displayUnit = unitInfo ? unitInfo.display_name : ingredientUnit;
    
    setMealIngredients(prev => [...prev, {
      food_id: addingIngredient.food_id || addingIngredient.fdc_id,
      name: addingIngredient.name || addingIngredient.food_name,
      quantity: parseFloat(ingredientQuantity),
      unit: ingredientUnit,
      displayUnit,
      macros
    }]);
    
    setAddingIngredient(null);
    setIngredientQuantity('1');
    toast.success(`Added ${addingIngredient.name || addingIngredient.food_name}`);
  };

  // Remove ingredient from meal
  const removeIngredient = (index) => {
    setMealIngredients(prev => prev.filter((_, i) => i !== index));
  };

  // Update food macros for single food logging
  const updateFoodMacros = (qty, unit) => {
    if (!selectedItem || selectedItem.itemType !== 'food' || !qty) return;
    const qtyNum = parseFloat(qty) || 0;
    if (qtyNum <= 0) return;
    const unitInfo = selectedItem.units.find(u => u.unit === unit);
    if (!unitInfo) return;
    const grams = qtyNum * unitInfo.grams_per_unit;
    const factor = grams / 100;
    setCalculatedMacros({
      calories: Math.round(selectedItem.calories_per_100g * factor),
      protein: Math.round(selectedItem.protein_per_100g * factor * 10) / 10,
      carbs: Math.round(selectedItem.carbs_per_100g * factor * 10) / 10,
      fat: Math.round(selectedItem.fat_per_100g * factor * 10) / 10
    });
  };

  const confirmFood = () => {
    if (!selectedItem || !calculatedMacros) return;
    const unitInfo = selectedItem.units.find(u => u.unit === foodUnit);
    const displayName = unitInfo ? unitInfo.display_name : foodUnit;
    setName(`${selectedItem.name} (${foodQuantity} ${displayName})`);
    setCalories(String(calculatedMacros.calories));
    setProtein(String(Math.round(calculatedMacros.protein)));
    setCarbs(String(Math.round(calculatedMacros.carbs)));
    setFat(String(Math.round(calculatedMacros.fat)));
    setIsEditing(false);
    setMode('confirm');
  };

  const goManual = () => {
    setSelectedItem(null);
    setMealIngredients([]);
    setMealImageUrl('');
    setName(''); setCalories(''); setProtein(''); setCarbs(''); setFat('');
    setMode('manual');
  };

  // Start building a custom meal from ingredients
  const startMealBuilder = () => {
    setSelectedItem(null);
    setMealIngredients([]);
    setMealImageUrl('');
    setName('');
    setCalories('0'); setProtein('0'); setCarbs('0'); setFat('0');
    setIsEditingMacros(false);
    setSaveToMyMeals(true);
    setMode('meal-builder');
  };

  const backToSearch = () => {
    setMode('search');
    setSelectedItem(null);
    setAddingIngredient(null);
    setMealIngredients([]);
    setMealImageUrl('');
    setIsEditingMacros(false);
  };

  // Proceed to confirm built meal
  const proceedToConfirmMeal = () => {
    if (mealIngredients.length === 0) {
      toast.error('Add at least one ingredient');
      return;
    }
    if (!name.trim()) {
      toast.error('Enter a meal name');
      return;
    }
    setSelectedItem({ itemType: 'custom-meal', ingredients: mealIngredients });
    setMode('confirm');
  };

  const handleSubmit = () => {
    if (!name.trim()) { toast.error('Please enter a name'); return; }
    const cal = parseInt(calories) || 0;
    const prot = parseInt(protein) || 0;
    const carb = parseInt(carbs) || 0;
    const fatVal = parseInt(fat) || 0;
    if (cal === 0 && prot === 0 && carb === 0 && fatVal === 0) { toast.error('Enter at least one macro'); return; }
    
    setSubmitting(true);
    
    // Macros object to store in meal plan
    const macrosObj = { calories: cal, protein: prot, carbs: carb, fat: fatVal };
    
    let mealIdPromise;
    
    if (selectedItem?.itemType === 'meal') {
      // Existing meal from library - no need to store macros in plan
      mealIdPromise = Promise.resolve(selectedItem.meal_id);
    } else if (selectedItem?.itemType === 'custom-meal' || selectedItem?.itemType === 'food') {
      // Custom meal with ingredients or single food
      const ingredientNames = selectedItem?.itemType === 'custom-meal' 
        ? mealIngredients.map(ing => `${ing.quantity} ${ing.displayUnit} ${ing.name}`)
        : selectedItem?.itemType === 'food' ? [selectedItem.name] : [];
      
      // Only save to library if toggle is enabled
      const shouldSaveToLibrary = saveToMyMeals;
      
      mealIdPromise = mealAPI.createCustomMeal({
        name: name.trim(), 
        category: mealType, 
        calories: cal, 
        protein: prot, 
        carbs: carb, 
        fat: fatVal,
        ingredients: ingredientNames,
        ingredient_details: selectedItem?.itemType === 'custom-meal' ? mealIngredients : [],
        image_url: mealImageUrl || null,
        save_to_library: shouldSaveToLibrary
      }).then(res => res.data.meal_id);
    } else if (saveToLibrary) {
      // Manual entry with "save to library" checked
      mealIdPromise = mealAPI.createCustomMeal({
        name: name.trim(), 
        category: mealType, 
        calories: cal, 
        protein: prot, 
        carbs: carb, 
        fat: fatVal,
        ingredients: [],
        ingredient_details: [],
        image_url: null,
        save_to_library: true
      }).then(res => res.data.meal_id);
    } else {
      // Quick manual entry - not saved to library
      mealIdPromise = Promise.resolve('quick_' + Date.now());
    }
    
    mealIdPromise
      .then(mealId => {
        // Include macros in meal plan for custom/quick meals so they display properly
        const planData = { 
          date, 
          meal_type: mealType, 
          meal_id: mealId, 
          meal_name: name.trim(), 
          status: 'completed', 
          source: 'manual'
        };
        
        // Add macros to plan for custom meals (so they display correctly even without meal lookup)
        if (selectedItem?.itemType !== 'meal') {
          planData.macros = macrosObj;
        }
        
        return mealPlanAPI.addMealPlan(planData);
      })
      .then(() => { 
        toast.success('Food logged!' + (saveToMyMeals && selectedItem?.itemType === 'custom-meal' ? ' Saved to My Meals.' : '')); 
        if (returnPath) {
          navigate(returnPath);
        } else {
          navigate(-1);
        }
      })
      .catch(err => { console.error(err); toast.error('Failed to log'); })
      .finally(() => setSubmitting(false));
  };

  const formatDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const goBack = () => {
    if (mode === 'search') {
      navigate(-1);
    } else if (mode === 'meal-builder' && addingIngredient) {
      setAddingIngredient(null);
    } else {
      backToSearch();
    }
  };

  // Get current preview macros for ingredient being added
  const previewMacros = addingIngredient 
    ? getIngredientMacros(addingIngredient, ingredientQuantity, ingredientUnit)
    : null;

  // Pre-render food cards for Foods tab (single item logging) - handles both old and new format
  const foodCards = filteredFoods.slice(0, 25).map(item => {
    // Handle new format (from search API) vs old format (from allFoods)
    const isNewFormat = item.cal_per_g !== undefined;
    // Use display_name if available (clean consumer-friendly name), fallback to food_name/name
    const foodName = item.display_name || (isNewFormat ? item.food_name : item.name);
    const foodId = item.food_id || item.fdc_id;
    
    let cal, prot, carb, fat;
    if (isNewFormat) {
      // New format: preview values per 100g
      cal = item.preview_calories ?? Math.round((item.cal_per_g || 0) * 100);
      prot = item.preview_protein ?? Math.round((item.protein_per_g || 0) * 100);
      carb = item.preview_carbs ?? Math.round((item.carbs_per_g || 0) * 100);
      fat = item.preview_fat ?? Math.round((item.fat_per_g || 0) * 100);
    } else {
      // Old format
      const factor = (item.default_serving_grams || 100) / 100;
      cal = Math.round((item.calories_per_100g || 0) * factor);
      prot = Math.round((item.protein_per_100g || 0) * factor);
      carb = Math.round((item.carbs_per_100g || 0) * factor);
      fat = Math.round((item.fat_per_100g || 0) * factor);
    }
    
    return (
      <button key={foodId} onClick={() => selectFood(item)} className="w-full p-4 bg-card border border-border/40 rounded-lg hover:border-green-500/50 transition-all text-left" data-testid={`food-${foodId}`}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Apple size={14} className="text-green-500" />
              <p className="font-medium">{foodName}</p>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{cal} cal • {prot}g P • {carb}g C • {fat}g F</p>
          </div>
          <ChevronRight size={18} className="text-muted-foreground" />
        </div>
      </button>
    );
  });

  // Pre-render existing meal cards (quick log existing recipe)
  const existingMealCards = filteredMeals.slice(0, 20).map(item => (
    <button key={item.meal_id} onClick={() => selectExistingMeal(item)} className="w-full p-4 bg-card border border-border/40 rounded-lg hover:border-accent/50 transition-all text-left" data-testid={`meal-${item.meal_id}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Utensils size={14} className="text-accent" />
            <p className="font-medium">{item.name}</p>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{item.calories} cal • {item.protein}g P • {item.category}</p>
        </div>
        <ChevronRight size={18} className="text-muted-foreground" />
      </div>
    </button>
  ));

  // Pre-render food cards for meal builder (add to ingredients) - handles both old and new format
  const ingredientFoodCards = filteredFoods.slice(0, 20).map(item => {
    // Handle new format (from search API) vs old format (from allFoods)
    const isNewFormat = item.cal_per_g !== undefined;
    // Use display_name if available (clean consumer-friendly name)
    const foodName = item.display_name || (isNewFormat ? item.food_name : item.name);
    const foodId = item.food_id || item.fdc_id;
    
    let cal, prot, carb, fat;
    if (isNewFormat) {
      // New format: preview values per 100g
      cal = item.preview_calories ?? Math.round((item.cal_per_g || 0) * 100);
      prot = item.preview_protein ?? Math.round((item.protein_per_g || 0) * 100);
      carb = item.preview_carbs ?? Math.round((item.carbs_per_g || 0) * 100);
      fat = item.preview_fat ?? Math.round((item.fat_per_g || 0) * 100);
    } else {
      // Old format
      const factor = (item.default_serving_grams || 100) / 100;
      cal = Math.round((item.calories_per_100g || 0) * factor);
      prot = Math.round((item.protein_per_100g || 0) * factor);
      carb = Math.round((item.carbs_per_100g || 0) * factor);
      fat = Math.round((item.fat_per_100g || 0) * factor);
    }
    
    const isAdded = mealIngredients.some(ing => ing.food_id === foodId);
    return (
      <button 
        key={foodId} 
        onClick={() => startAddIngredient(item)} 
        className={`w-full p-3 bg-card border rounded-lg transition-all text-left ${isAdded ? 'border-green-500/50 bg-green-500/5' : 'border-border/40 hover:border-accent/50'}`}
        data-testid={`ingredient-${foodId}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Apple size={14} className="text-green-500" />
              <p className="font-medium text-sm">{foodName}</p>
              {isAdded && <Check size={14} className="text-green-500" />}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{cal} cal • {prot}g P • {carb}g C • {fat}g F</p>
          </div>
          <Plus size={18} className="text-accent" />
        </div>
      </button>
    );
  });

  // Pre-render unit options
  const unitOptions = selectedItem?.units?.map(u => <option key={u.unit} value={u.unit}>{u.display_name}</option>) || [];
  const ingredientUnitOptions = addingIngredient?.units?.map(u => <option key={u.unit} value={u.unit}>{u.display_name}</option>) || [];

  // Pre-render meal type buttons
  const typeButtons = mealTypes.map(t => (
    <button key={t} onClick={() => setMealType(t)} className={`py-2 px-3 rounded-lg border text-sm font-medium transition-all ${mealType === t ? 'border-accent bg-accent/10 text-accent' : 'border-border/40'}`} data-testid={`type-${t.toLowerCase()}`}>{t}</button>
  ));

  // Calculate totals for display
  const totalMacros = calculateTotalMacros();

  return (
    <div className="min-h-screen pb-bottom-nav bg-background">
      <div className="sticky top-0 z-10 bg-background border-b border-border/40 p-4 header-safe-top">
        <div className="flex items-center gap-3">
          <Button onClick={goBack} variant="ghost" size="icon" className="rounded-full h-9 w-9" data-testid="back-btn"><ArrowLeft size={18} /></Button>
          <div className="flex-1">
            <h1 className="font-heading font-bold text-xl">Log Food</h1>
            <p className="text-xs text-muted-foreground">
              {mode === 'search' ? 'Search meals or foods' : 
               mode === 'food-detail' ? 'Set quantity' : 
               mode === 'meal-builder' ? (addingIngredient ? 'Set quantity' : 'Build your meal') :
               mode === 'confirm' ? 'Confirm & log' : 'Enter details'}
            </p>
          </div>
        </div>
      </div>

      {/* SEARCH MODE */}
      {mode === 'search' && (
        <div className="p-4 space-y-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <input 
              type="text" 
              value={searchQuery} 
              onChange={e => setSearchQuery(e.target.value)} 
              onFocus={() => setShowSearchHistory(true)}
              onBlur={() => setTimeout(() => setShowSearchHistory(false), 200)}
              placeholder="Search foods or meals..." 
              className="w-full h-12 pl-11 pr-4 rounded-xl border border-border/40 bg-card focus:outline-none focus:ring-2 focus:ring-accent/50" 
              data-testid="search-input" 
              autoFocus 
            />
            
            {/* Search History Dropdown */}
            {showSearchHistory && !searchQuery && searchHistory.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border/40 rounded-xl shadow-lg z-50 overflow-hidden" data-testid="search-history-dropdown">
                <div className="p-2 border-b border-border/40 flex items-center justify-between">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide px-2">Recent Searches</p>
                  <button 
                    onClick={(e) => { e.stopPropagation(); foodsAPI.clearSearchHistory(); setSearchHistory([]); }}
                    className="text-xs text-muted-foreground hover:text-foreground px-2"
                  >
                    Clear
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {searchHistory.map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => { setSearchQuery(item.query); setShowSearchHistory(false); }}
                      className="w-full p-3 text-left hover:bg-secondary/30 flex items-center gap-3 transition-colors"
                      data-testid={`history-${idx}`}
                    >
                      <Clock size={14} className="text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium capitalize truncate">{item.query}</p>
                        {item.last_food_name && (
                          <p className="text-xs text-muted-foreground truncate">Last: {item.last_food_name}</p>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">{item.count}x</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Tabs for Foods / Meals */}
          <div className="flex gap-2 p-1 bg-secondary/30 rounded-xl">
            <button 
              onClick={() => setActiveTab('foods')} 
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${activeTab === 'foods' ? 'bg-green-500 text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="tab-foods"
            >
              <Apple size={16} />
              <span>Foods</span>
            </button>
            <button 
              onClick={() => setActiveTab('meals')} 
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${activeTab === 'meals' ? 'bg-accent text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="tab-meals"
            >
              <Utensils size={16} />
              <span>Meals</span>
            </button>
          </div>

          {activeTab === 'foods' ? (
            <>
              {/* Manual entry for Foods tab */}
              <button onClick={goManual} className="w-full p-4 bg-secondary/30 hover:bg-secondary/50 rounded-xl flex items-center gap-3 transition-colors" data-testid="manual-entry-btn">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center"><Edit3 size={18} className="text-accent" /></div>
                <div className="flex-1 text-left"><p className="font-medium">Enter Manually</p><p className="text-xs text-muted-foreground">Quick log with custom macros</p></div>
                <ChevronRight size={18} className="text-muted-foreground" />
              </button>
              
              {/* Recently Logged Foods - only show when not searching */}
              {!searchQuery && recentFoods.length > 0 && (
                <div className="space-y-2" data-testid="recent-foods-section">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide px-1">Recently Logged</p>
                  <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
                    {recentFoods.map(food => {
                      const foodId = food.food_id || food.fdc_id;
                      const displayName = food.display_name || food.food_name;
                      const cal = food.preview_calories ?? Math.round((food.cal_per_g || 0) * 100);
                      const prot = food.preview_protein ?? Math.round((food.protein_per_g || 0) * 100);
                      return (
                        <button 
                          key={foodId}
                          onClick={() => selectFood(food)}
                          className="flex-shrink-0 p-3 bg-card border border-border/40 rounded-xl hover:border-green-500/50 transition-all text-left min-w-[140px] max-w-[180px]"
                          data-testid={`recent-${foodId}`}
                        >
                          <p className="font-medium text-sm truncate">{displayName}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{cal} cal • {prot}g P</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wide px-1">
                  {searchQuery ? (isSearching ? 'Searching...' : 'Search Results') : 'Core Foods'}
                </p>
                {(searchLoading || isSearching) ? (
                  <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" /></div>
                ) : foodCards.length > 0 ? (
                  <div className="space-y-2">{foodCards}</div>
                ) : (
                  <div className="text-center py-8">
                    <Apple size={32} className="mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-muted-foreground text-sm">{searchQuery ? 'No foods found for "' + searchQuery + '"' : 'No foods found'}</p>
                    <Button onClick={goManual} size="sm" className="mt-3 rounded-full">Enter Manually</Button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Build Custom Meal option for Meals tab */}
              <button onClick={startMealBuilder} className="w-full p-4 bg-accent/10 hover:bg-accent/20 border border-accent/30 rounded-xl flex items-center gap-3 transition-colors" data-testid="build-meal-btn">
                <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center"><Plus size={18} className="text-accent" /></div>
                <div className="flex-1 text-left"><p className="font-medium text-accent">Build Custom Meal</p><p className="text-xs text-muted-foreground">Combine ingredients with auto-calculated macros</p></div>
                <ChevronRight size={18} className="text-accent" />
              </button>

              {/* My Meals Section */}
              {myMeals.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide px-1">
                    My Meals
                  </p>
                  <div className="space-y-2">
                    {myMeals.map(function(meal) {
                      return (
                        <button
                          key={meal.meal_id}
                          onClick={function() { selectExistingMeal(meal); }}
                          className="w-full p-3 bg-card border border-border/40 rounded-xl hover:border-accent/50 transition-all flex items-center gap-3 text-left"
                          data-testid={'my-meal-' + meal.meal_id}
                        >
                          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center flex-shrink-0">
                            {meal.image_url ? (
                              <img src={meal.image_url} alt={meal.name} className="w-full h-full object-cover rounded-lg" />
                            ) : (
                              <Utensils size={20} className="text-accent" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{meal.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {Math.round(meal.calories)} cal • {Math.round(meal.protein)}g P • {Math.round(meal.carbs)}g C • {Math.round(meal.fat)}g F
                            </p>
                          </div>
                          <ChevronRight size={16} className="text-muted-foreground flex-shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wide px-1">
                  {searchQuery ? 'Meal Results' : 'Quick Log from Library'}
                </p>
                {searchLoading ? (
                  <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" /></div>
                ) : existingMealCards.length > 0 ? (
                  <div className="space-y-2">{existingMealCards}</div>
                ) : (
                  <div className="text-center py-8">
                    <Utensils size={32} className="mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-muted-foreground text-sm">No meals found</p>
                    <Button onClick={startMealBuilder} size="sm" className="mt-3 rounded-full bg-accent">Build Custom Meal</Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* MEAL BUILDER MODE */}
      {mode === 'meal-builder' && !addingIngredient && (
        <div className="p-4 space-y-4">
          {/* Meal Name Input */}
          <div>
            <label className="text-sm font-medium">Meal Name</label>
            <input 
              type="text" 
              value={name} 
              onChange={e => setName(e.target.value)} 
              placeholder="e.g., Chicken & Rice Bowl" 
              className="w-full h-12 px-4 rounded-xl border border-border/40 bg-card mt-2 focus:outline-none focus:ring-2 focus:ring-accent/50" 
              data-testid="meal-name-input"
            />
          </div>

          {/* Ingredients List */}
          <div className="bg-card border border-border/40 rounded-xl overflow-hidden">
            <div className="p-3 bg-secondary/30 border-b border-border/40 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Utensils size={16} className="text-accent" />
                <span className="font-medium text-sm">Ingredients</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-accent/20 text-accent">{mealIngredients.length}</span>
              </div>
            </div>
            
            {mealIngredients.length > 0 ? (
              <div className="divide-y divide-border/40">
                {mealIngredients.map((ing, idx) => (
                  <div key={idx} className="p-3 flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{ing.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {ing.quantity} {ing.displayUnit} • {ing.macros.calories} cal • {ing.macros.protein}g P
                      </p>
                    </div>
                    <button 
                      onClick={() => removeIngredient(idx)} 
                      className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"
                      data-testid={`remove-ingredient-${idx}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-muted-foreground">
                <Apple size={24} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No ingredients added yet</p>
              </div>
            )}
          </div>

          {/* Running Total Macros */}
          {mealIngredients.length > 0 && (
            <div className="p-4 rounded-xl bg-accent/5 border border-accent/20">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium">Total Nutrition</p>
                <button 
                  onClick={() => setIsEditingMacros(!isEditingMacros)}
                  className="text-xs text-accent flex items-center gap-1"
                  data-testid="edit-macros-btn"
                >
                  <Edit3 size={12} />
                  {isEditingMacros ? 'Done Editing' : 'Edit Macros'}
                </button>
              </div>
              
              {isEditingMacros ? (
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <label className="text-xs text-muted-foreground">Cal</label>
                    <input 
                      type="number" 
                      value={calories} 
                      onChange={e => setCalories(e.target.value)} 
                      className="w-full h-10 px-2 rounded-lg border border-border/40 bg-card text-center text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Protein</label>
                    <input 
                      type="number" 
                      value={protein} 
                      onChange={e => setProtein(e.target.value)} 
                      className="w-full h-10 px-2 rounded-lg border border-border/40 bg-card text-center text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Carbs</label>
                    <input 
                      type="number" 
                      value={carbs} 
                      onChange={e => setCarbs(e.target.value)} 
                      className="w-full h-10 px-2 rounded-lg border border-border/40 bg-card text-center text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Fat</label>
                    <input 
                      type="number" 
                      value={fat} 
                      onChange={e => setFat(e.target.value)} 
                      className="w-full h-10 px-2 rounded-lg border border-border/40 bg-card text-center text-sm"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-3 text-center">
                  <div>
                    <div className="w-9 h-9 rounded-full bg-orange-500/10 flex items-center justify-center mx-auto mb-1"><Flame size={14} className="text-orange-500" /></div>
                    <p className="font-semibold text-sm">{totalMacros.calories}</p>
                    <p className="text-xs text-muted-foreground">cal</p>
                  </div>
                  <div>
                    <div className="w-9 h-9 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-1"><Beef size={14} className="text-red-500" /></div>
                    <p className="font-semibold text-sm">{Math.round(totalMacros.protein)}g</p>
                    <p className="text-xs text-muted-foreground">protein</p>
                  </div>
                  <div>
                    <div className="w-9 h-9 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-1"><Wheat size={14} className="text-amber-500" /></div>
                    <p className="font-semibold text-sm">{Math.round(totalMacros.carbs)}g</p>
                    <p className="text-xs text-muted-foreground">carbs</p>
                  </div>
                  <div>
                    <div className="w-9 h-9 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-1"><Droplet size={14} className="text-blue-500" /></div>
                    <p className="font-semibold text-sm">{Math.round(totalMacros.fat)}g</p>
                    <p className="text-xs text-muted-foreground">fat</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Search Foods to Add */}
          <div>
            <div className="relative mb-3">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
              <input 
                type="text" 
                value={searchQuery} 
                onChange={e => setSearchQuery(e.target.value)} 
                placeholder="Search foods to add..." 
                className="w-full h-10 pl-10 pr-4 rounded-lg border border-border/40 bg-card text-sm focus:outline-none focus:ring-2 focus:ring-accent/50" 
                data-testid="ingredient-search"
              />
            </div>
            
            <p className="text-xs text-muted-foreground uppercase tracking-wide px-1 mb-2">
              {searchQuery ? 'Search Results' : 'Add Ingredients'}
            </p>
            
            <div className="space-y-2 max-h-[40vh] overflow-y-auto">
              {ingredientFoodCards}
            </div>
          </div>

          {/* Image URL Input (Optional) */}
          <div>
            <label className="text-sm font-medium flex items-center gap-2">
              <Image size={14} className="text-muted-foreground" />
              Image URL (optional)
            </label>
            <input 
              type="text" 
              value={mealImageUrl} 
              onChange={e => setMealImageUrl(e.target.value)} 
              placeholder="https://example.com/meal-image.jpg" 
              className="w-full h-10 px-4 rounded-lg border border-border/40 bg-card mt-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50" 
              data-testid="meal-image-url"
            />
            <p className="text-xs text-muted-foreground mt-1">Paste an image URL or leave empty for a placeholder</p>
          </div>

          {/* Save to My Meals Toggle */}
          <label className="flex items-center gap-3 p-3 rounded-xl bg-secondary/20 cursor-pointer" data-testid="save-to-my-meals-toggle">
            <input 
              type="checkbox" 
              checked={saveToMyMeals} 
              onChange={e => setSaveToMyMeals(e.target.checked)} 
              className="w-5 h-5 rounded border-border/40 text-accent accent-accent" 
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Save size={14} className="text-accent" />
                <span className="text-sm font-medium">Save to My Meals</span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">Reuse this meal for future planning</p>
            </div>
          </label>

          {/* Continue Button */}
          <Button 
            onClick={proceedToConfirmMeal} 
            disabled={mealIngredients.length === 0 || !name.trim()}
            className="w-full h-12 rounded-xl bg-accent hover:bg-accent/90 text-white font-semibold" 
            data-testid="continue-meal-btn"
          >
            Continue to Log
          </Button>
        </div>
      )}

      {/* ADD INGREDIENT QUANTITY MODAL */}
      {mode === 'meal-builder' && addingIngredient && (
        <div className="p-4 space-y-4">
          <div className="p-4 rounded-xl bg-green-500/5 border border-green-500/20">
            <div className="flex items-center gap-2 mb-2"><Apple size={18} className="text-green-500" /><p className="font-heading font-semibold">{addingIngredient.name}</p></div>
            <p className="text-xs text-muted-foreground">{addingIngredient.category}</p>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Quantity</label>
            <div className="flex gap-3">
              <input 
                type="number" 
                value={ingredientQuantity} 
                onChange={e => setIngredientQuantity(e.target.value)} 
                min="0.1" 
                step="0.5" 
                className="flex-1 h-12 px-4 rounded-xl border border-border/40 bg-card" 
                data-testid="ingredient-quantity-input" 
              />
              <select 
                value={ingredientUnit} 
                onChange={e => setIngredientUnit(e.target.value)} 
                className="flex-1 h-12 px-4 rounded-xl border border-border/40 bg-card" 
                data-testid="ingredient-unit-select"
              >
                {ingredientUnitOptions}
              </select>
            </div>
          </div>
          
          {previewMacros && (
            <div className="p-4 rounded-xl bg-card border border-border/40">
              <p className="text-xs text-muted-foreground mb-3">Nutrition for this amount</p>
              <div className="grid grid-cols-4 gap-3 text-center">
                <div><div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center mx-auto mb-1"><Flame size={16} className="text-orange-500" /></div><p className="font-semibold">{previewMacros.calories}</p><p className="text-xs text-muted-foreground">cal</p></div>
                <div><div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-1"><Beef size={16} className="text-red-500" /></div><p className="font-semibold">{previewMacros.protein}g</p><p className="text-xs text-muted-foreground">protein</p></div>
                <div><div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-1"><Wheat size={16} className="text-amber-500" /></div><p className="font-semibold">{previewMacros.carbs}g</p><p className="text-xs text-muted-foreground">carbs</p></div>
                <div><div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-1"><Droplet size={16} className="text-blue-500" /></div><p className="font-semibold">{previewMacros.fat}g</p><p className="text-xs text-muted-foreground">fat</p></div>
              </div>
            </div>
          )}
          
          <div className="flex gap-3">
            <Button onClick={() => setAddingIngredient(null)} variant="outline" className="flex-1 h-12 rounded-xl">Cancel</Button>
            <Button onClick={confirmAddIngredient} className="flex-1 h-12 rounded-xl bg-accent hover:bg-accent/90 text-white font-semibold" data-testid="add-ingredient-btn">Add to Meal</Button>
          </div>
        </div>
      )}

      {/* SINGLE FOOD DETAIL MODE (Foods tab) */}
      {mode === 'food-detail' && selectedItem && (
        <div className="p-4 space-y-4">
          <div className="p-4 rounded-xl bg-green-500/5 border border-green-500/20">
            <div className="flex items-center gap-2 mb-2"><Apple size={18} className="text-green-500" /><p className="font-heading font-semibold">{selectedItem.name}</p></div>
            <p className="text-xs text-muted-foreground">{selectedItem.category}</p>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Quantity</label>
            <div className="flex gap-3">
              <input type="number" value={foodQuantity} onChange={e => { setFoodQuantity(e.target.value); updateFoodMacros(e.target.value, foodUnit); }} min="0.1" step="0.5" className="flex-1 h-12 px-4 rounded-xl border border-border/40 bg-card" data-testid="food-quantity-input" />
              <select value={foodUnit} onChange={e => { setFoodUnit(e.target.value); updateFoodMacros(foodQuantity, e.target.value); }} className="flex-1 h-12 px-4 rounded-xl border border-border/40 bg-card" data-testid="food-unit-select">{unitOptions}</select>
            </div>
          </div>
          {calculatedMacros && (
            <div className="p-4 rounded-xl bg-card border border-border/40">
              <p className="text-xs text-muted-foreground mb-3">Nutrition</p>
              <div className="grid grid-cols-4 gap-3 text-center">
                <div><div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center mx-auto mb-1"><Flame size={16} className="text-orange-500" /></div><p className="font-semibold">{calculatedMacros.calories}</p><p className="text-xs text-muted-foreground">cal</p></div>
                <div><div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-1"><Beef size={16} className="text-red-500" /></div><p className="font-semibold">{calculatedMacros.protein}g</p><p className="text-xs text-muted-foreground">protein</p></div>
                <div><div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-1"><Wheat size={16} className="text-amber-500" /></div><p className="font-semibold">{calculatedMacros.carbs}g</p><p className="text-xs text-muted-foreground">carbs</p></div>
                <div><div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-1"><Droplet size={16} className="text-blue-500" /></div><p className="font-semibold">{calculatedMacros.fat}g</p><p className="text-xs text-muted-foreground">fat</p></div>
              </div>
            </div>
          )}
          <Button onClick={confirmFood} className="w-full h-12 rounded-xl bg-accent hover:bg-accent/90 text-white font-semibold" data-testid="confirm-food-btn">Continue to Log</Button>
        </div>
      )}

      {/* CONFIRM / MANUAL MODE */}
      {(mode === 'confirm' || mode === 'manual') && (
        <div className="p-4 space-y-4">
          {selectedItem && mode === 'confirm' && (
            <div className={`p-4 rounded-xl ${selectedItem.itemType === 'food' ? 'bg-green-500/5 border-green-500/20' : 'bg-accent/5 border-accent/20'} border`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {selectedItem.itemType === 'food' ? <Apple size={14} className="text-green-500" /> : <Utensils size={14} className="text-accent" />}
                  <p className="font-heading font-semibold">{name}</p>
                </div>
                {selectedItem.itemType !== 'custom-meal' && (
                  <Button onClick={() => setIsEditing(!isEditing)} variant="ghost" size="sm" className={selectedItem.itemType === 'food' ? 'text-green-600' : 'text-accent'}>
                    <Edit3 size={14} className="mr-1" />{isEditing ? 'Done' : 'Edit'}
                  </Button>
                )}
              </div>
              
              {/* Show ingredients for custom meals */}
              {selectedItem.itemType === 'custom-meal' && mealIngredients.length > 0 && (
                <div className="mt-3 pt-3 border-t border-border/20">
                  <p className="text-xs text-muted-foreground mb-2">Ingredients:</p>
                  <div className="flex flex-wrap gap-1">
                    {mealIngredients.map((ing, idx) => (
                      <span key={idx} className="text-xs px-2 py-1 bg-secondary/50 rounded-full">
                        {ing.quantity} {ing.displayUnit} {ing.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Inline quantity/unit editing for food items */}
              {isEditing && selectedItem.itemType === 'food' && (
                <div className="mt-3 pt-3 border-t border-border/20">
                  <label className="text-xs text-muted-foreground">Adjust quantity</label>
                  <div className="flex gap-2 mt-1">
                    <input 
                      type="number" 
                      value={foodQuantity} 
                      onChange={e => { 
                        setFoodQuantity(e.target.value); 
                        updateFoodMacros(e.target.value, foodUnit);
                        const qtyNum = parseFloat(e.target.value) || 0;
                        const unitInfo = selectedItem.units.find(u => u.unit === foodUnit);
                        if (unitInfo && qtyNum > 0) {
                          const grams = qtyNum * unitInfo.grams_per_unit;
                          const factor = grams / 100;
                          setCalories(String(Math.round(selectedItem.calories_per_100g * factor)));
                          setProtein(String(Math.round(selectedItem.protein_per_100g * factor)));
                          setCarbs(String(Math.round(selectedItem.carbs_per_100g * factor)));
                          setFat(String(Math.round(selectedItem.fat_per_100g * factor)));
                          const displayName = unitInfo.display_name;
                          setName(`${selectedItem.name} (${e.target.value} ${displayName})`);
                        }
                      }} 
                      min="0.1" 
                      step="0.5" 
                      className="flex-1 h-10 px-3 rounded-lg border border-border/40 bg-card text-sm" 
                    />
                    <select 
                      value={foodUnit} 
                      onChange={e => { 
                        setFoodUnit(e.target.value); 
                        updateFoodMacros(foodQuantity, e.target.value);
                        const qtyNum = parseFloat(foodQuantity) || 0;
                        const unitInfo = selectedItem.units.find(u => u.unit === e.target.value);
                        if (unitInfo && qtyNum > 0) {
                          const grams = qtyNum * unitInfo.grams_per_unit;
                          const factor = grams / 100;
                          setCalories(String(Math.round(selectedItem.calories_per_100g * factor)));
                          setProtein(String(Math.round(selectedItem.protein_per_100g * factor)));
                          setCarbs(String(Math.round(selectedItem.carbs_per_100g * factor)));
                          setFat(String(Math.round(selectedItem.fat_per_100g * factor)));
                          const displayName = unitInfo.display_name;
                          setName(`${selectedItem.name} (${foodQuantity} ${displayName})`);
                        }
                      }} 
                      className="flex-1 h-10 px-3 rounded-lg border border-border/40 bg-card text-sm"
                    >
                      {unitOptions}
                    </select>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {mode === 'manual' && (
            <div>
              <label className="text-sm font-medium">Food Name</label>
              <div className="mt-2 relative">
                <Utensils size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Grilled Chicken" className="w-full h-12 pl-11 pr-4 rounded-xl border border-border/40 bg-card" data-testid="name-input" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium flex items-center gap-2"><Flame size={14} className="text-orange-500" />Calories</label>
              <input type="number" value={calories} onChange={e => setCalories(e.target.value)} placeholder="0" className="w-full h-12 px-4 rounded-xl border border-border/40 bg-card mt-2" readOnly={mode === 'confirm' && !isEditing && selectedItem?.itemType !== 'custom-meal'} data-testid="calories-input" />
            </div>
            <div>
              <label className="text-sm font-medium flex items-center gap-2"><Beef size={14} className="text-red-500" />Protein (g)</label>
              <input type="number" value={protein} onChange={e => setProtein(e.target.value)} placeholder="0" className="w-full h-12 px-4 rounded-xl border border-border/40 bg-card mt-2" readOnly={mode === 'confirm' && !isEditing && selectedItem?.itemType !== 'custom-meal'} data-testid="protein-input" />
            </div>
            <div>
              <label className="text-sm font-medium flex items-center gap-2"><Wheat size={14} className="text-amber-500" />Carbs (g)</label>
              <input type="number" value={carbs} onChange={e => setCarbs(e.target.value)} placeholder="0" className="w-full h-12 px-4 rounded-xl border border-border/40 bg-card mt-2" readOnly={mode === 'confirm' && !isEditing && selectedItem?.itemType !== 'custom-meal'} data-testid="carbs-input" />
            </div>
            <div>
              <label className="text-sm font-medium flex items-center gap-2"><Droplet size={14} className="text-blue-500" />Fat (g)</label>
              <input type="number" value={fat} onChange={e => setFat(e.target.value)} placeholder="0" className="w-full h-12 px-4 rounded-xl border border-border/40 bg-card mt-2" readOnly={mode === 'confirm' && !isEditing && selectedItem?.itemType !== 'custom-meal'} data-testid="fat-input" />
            </div>
          </div>

          <div><label className="text-sm font-medium">Meal Type</label><div className="mt-2 flex gap-2">{typeButtons}</div></div>
          <div><label className="text-sm font-medium">Date</label><p className="mt-2 p-3 rounded-xl bg-card border border-border/40">{formatDate(date)}</p></div>
          
          {mode === 'manual' && !selectedItem && (
            <label className="flex items-center gap-3 p-3 rounded-xl bg-secondary/20 cursor-pointer">
              <input type="checkbox" checked={saveToLibrary} onChange={e => setSaveToLibrary(e.target.checked)} className="w-5 h-5 rounded border-border/40 text-accent" />
              <span className="text-sm">Save to my library for future use</span>
            </label>
          )}

          <Button onClick={handleSubmit} disabled={submitting} className="w-full h-12 rounded-xl bg-accent hover:bg-accent/90 text-white font-semibold" data-testid="submit-btn">
            {submitting ? 'Logging...' : 'Log Food'}
          </Button>
        </div>
      )}

      <BottomNav />
    </div>
  );
}

export default AddCustomMeal;
